import { useState, useEffect, useRef } from 'react';
import api from '../../services/api';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, Pencil, History, Loader2, Search } from 'lucide-react';

const enconOrange = '#ee7203';
const QUOTE_UOM_OPTIONS = ['Each', 'M2', 'Length', 'M'];

/** Minimal product-code search, reused from the same /stock-database/search
 * endpoint that powers the Create Quote / Pro Forma code dropdown. Also usable
 * as an inline "correct this code" editor: prefill with `initialValue`, and
 * pressing Enter saves the typed text as-is via `onManualSave` (in case the
 * right code isn't surfaced by the search). */
const StockCodeSearch = ({ onSelect, initialValue = '', onManualSave, onCancel, testIdPrefix = 'pricing-add-line' }) => {
  const [query, setQuery] = useState(initialValue);
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const search = async (q) => {
    setQuery(q);
    if (!q || q.length < 2) { setResults([]); return; }
    setLoading(true);
    try {
      const res = await api.get(`${API}/stock-database/search`, { params: { q, limit: 20, include_all: true } });
      setResults(res.data?.results || []);
      setOpen(true);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative" ref={boxRef}>
      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
        <Input
          value={query}
          onChange={(e) => search(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && onManualSave) { e.preventDefault(); onManualSave(query); setOpen(false); }
            if (e.key === 'Escape' && onCancel) { e.preventDefault(); onCancel(); }
          }}
          placeholder="Search stock database by code or description..."
          className="pl-7 text-sm h-9"
          data-testid={`${testIdPrefix}-stock-search`}
          autoFocus={!!onManualSave}
        />
        {loading && <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 animate-spin text-gray-400" />}
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-50 mt-1 w-full max-h-64 overflow-y-auto bg-white border rounded-lg shadow-lg">
          {results.map((r, i) => (
            <button
              key={i}
              type="button"
              onClick={() => { onSelect(r); setOpen(false); setQuery(''); setResults([]); }}
              className="w-full text-left px-3 py-2 hover:bg-orange-50 border-b last:border-b-0 text-sm"
              data-testid={`${testIdPrefix}-result-${i}`}
            >
              <span className="font-semibold text-gray-800">{r.product_code}</span>
              <span className="text-gray-500 ml-2">{r.description}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const PricingProductTable = ({ category, onBack }) => {
  const [products, setProducts] = useState([]);
  const [rates, setRates] = useState({ total_discount_pct: 0, total_rebate_pct: 0 });
  const [loading, setLoading] = useState(true);
  const [addingLine, setAddingLine] = useState(false);
  const [newLine, setNewLine] = useState({ description: '', uom: 'Each', list_price: 0, stock_code: '' });
  const [editingProduct, setEditingProduct] = useState(null);
  const [editForm, setEditForm] = useState({ list_price: 0, effective_date: '', note: '' });
  const [historyFor, setHistoryFor] = useState(null);
  const [history, setHistory] = useState([]);
  const [editingCodeFor, setEditingCodeFor] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`${API}/pricing/categories/${category.id}/products`);
      setProducts(res.data?.products || []);
      setRates({ total_discount_pct: res.data?.total_discount_pct || 0, total_rebate_pct: res.data?.total_rebate_pct || 0 });
    } catch {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [category.id]);

  const saveStockCode = async (productId, code) => {
    try {
      await api.put(`${API}/pricing/products/${productId}`, { stock_code: code || '' });
      toast.success(code ? `Linked to ${code}` : 'Code unlinked');
      setEditingCodeFor(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update code');
    }
  };

  const submitNewLine = async () => {
    if (!newLine.description.trim()) { toast.error('Description is required'); return; }
    try {
      await api.post(`${API}/pricing/categories/${category.id}/products`, newLine);
      toast.success('Product line added');
      setAddingLine(false);
      setNewLine({ description: '', uom: 'Each', list_price: 0, stock_code: '' });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to add product line');
    }
  };

  const openEdit = (p) => {
    setEditingProduct(p);
    setEditForm({ list_price: p.list_price, effective_date: p.effective_date || '', note: '' });
  };

  const submitEdit = async () => {
    try {
      await api.put(`${API}/pricing/products/${editingProduct.id}`, editForm);
      toast.success('Product updated');
      setEditingProduct(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update product');
    }
  };

  const deleteProduct = async (p) => {
    if (!window.confirm(`Delete "${p.description}"?`)) return;
    try {
      await api.delete(`${API}/pricing/products/${p.id}`);
      toast.success('Product deleted');
      load();
    } catch {
      toast.error('Failed to delete product');
    }
  };

  const openHistory = async (p) => {
    setHistoryFor(p);
    try {
      const res = await api.get(`${API}/pricing/products/${p.id}/history`);
      setHistory(res.data?.history || []);
    } catch {
      setHistory([]);
    }
  };

  return (
    <div data-testid="pricing-product-table">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={onBack} data-testid="pricing-products-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Categories
          </Button>
          <h3 className="font-bold text-lg">{category.name}</h3>
          {(rates.total_discount_pct > 0 || rates.total_rebate_pct > 0) && (
            <span className="text-xs text-gray-500">
              (Discount {rates.total_discount_pct}% + Rebate {rates.total_rebate_pct}% applied to Net Cost below)
            </span>
          )}
        </div>
        <Button onClick={() => setAddingLine(true)} style={{ backgroundColor: enconOrange }} className="text-white" size="sm" data-testid="pricing-add-line-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Line
        </Button>
      </div>

      {addingLine && (
        <div className="mb-4 p-4 rounded-xl border-2 border-dashed" style={{ borderColor: enconOrange }} data-testid="pricing-add-line-form">
          <Label className="text-xs text-gray-600 mb-1 block">Search Stock Database (optional — links this price to a real product code)</Label>
          <StockCodeSearch onSelect={(p) => setNewLine(prev => ({ ...prev, stock_code: p.product_code, description: prev.description || p.description }))} />
          <div className="grid grid-cols-4 gap-2 mt-3">
            <div className="col-span-2">
              <Label className="text-xs text-gray-600">Description</Label>
              <Input value={newLine.description} onChange={(e) => setNewLine({ ...newLine, description: e.target.value })} data-testid="pricing-add-line-description" />
            </div>
            <div>
              <Label className="text-xs text-gray-600">UOM</Label>
              <Select value={newLine.uom} onValueChange={(v) => setNewLine({ ...newLine, uom: v })}>
                <SelectTrigger data-testid="pricing-add-line-uom"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {QUOTE_UOM_OPTIONS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-gray-600">List Price (£)</Label>
              <Input type="number" step="0.01" value={newLine.list_price} onChange={(e) => setNewLine({ ...newLine, list_price: parseFloat(e.target.value) || 0 })} data-testid="pricing-add-line-price" />
            </div>
          </div>
          {newLine.stock_code && <p className="text-xs text-gray-500 mt-2">Linked to stock code: <span className="font-semibold">{newLine.stock_code}</span></p>}
          <div className="flex gap-2 mt-3">
            <Button size="sm" onClick={submitNewLine} style={{ backgroundColor: enconOrange }} className="text-white" data-testid="pricing-add-line-submit">Add</Button>
            <Button size="sm" variant="outline" onClick={() => setAddingLine(false)}>Cancel</Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin" style={{ color: enconOrange }} /></div>
      ) : (
        <div className="rounded-xl border overflow-hidden overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow style={{ backgroundColor: enconOrange }}>
                <TableHead className="text-white">Description</TableHead>
                <TableHead className="text-white">Stock Code</TableHead>
                <TableHead className="text-white">UOM</TableHead>
                <TableHead className="text-white text-right">List Price</TableHead>
                <TableHead className="text-white text-right">Net Cost</TableHead>
                <TableHead className="text-white">Updated</TableHead>
                <TableHead className="text-white"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {products.map(p => (
                <TableRow key={p.id} data-testid={`pricing-product-row-${p.id}`}>
                  <TableCell className="max-w-xs">{p.description}</TableCell>
                  <TableCell className="min-w-[220px]">
                    {editingCodeFor === p.id ? (
                      <StockCodeSearch
                        initialValue={p.stock_code || ''}
                        testIdPrefix={`pricing-edit-code-${p.id}`}
                        onSelect={(product) => saveStockCode(p.id, product.product_code)}
                        onManualSave={(text) => saveStockCode(p.id, text.trim())}
                        onCancel={() => setEditingCodeFor(null)}
                      />
                    ) : (
                      <div className="flex items-center gap-1.5 cursor-pointer group" onClick={() => setEditingCodeFor(p.id)} data-testid={`pricing-stock-code-${p.id}`}>
                        {p.stock_code ? <span className="font-medium">{p.stock_code}</span> : <span className="text-gray-400 italic">unlinked</span>}
                        {p.stock_code_source === 'proposed' && (
                          <span className="text-[9px] font-semibold px-1 py-0.5 rounded bg-amber-100 text-amber-700" title="AI-proposed match — please verify">AI</span>
                        )}
                        <Pencil className="w-3 h-3 text-gray-300 group-hover:text-gray-500" />
                      </div>
                    )}
                  </TableCell>
                  <TableCell>{p.uom}</TableCell>
                  <TableCell className="text-right">£{(p.list_price || 0).toFixed(2)}</TableCell>
                  <TableCell className="text-right font-semibold" style={{ color: enconOrange }}>£{(p.net_cost || 0).toFixed(2)}</TableCell>
                  <TableCell className="text-xs text-gray-500">{p.updated_label}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <button onClick={() => openEdit(p)} className="p-1 text-gray-500 hover:bg-gray-100 rounded" title="Edit price" data-testid={`pricing-edit-product-${p.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                      <button onClick={() => openHistory(p)} className="p-1 text-gray-500 hover:bg-gray-100 rounded" title="Price history" data-testid={`pricing-history-product-${p.id}`}><History className="w-3.5 h-3.5" /></button>
                      <button onClick={() => deleteProduct(p)} className="p-1 text-red-500 hover:bg-red-50 rounded" title="Delete" data-testid={`pricing-delete-product-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {products.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center text-gray-400 py-6">No products in this category yet.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {editingProduct && (
        <Dialog open onOpenChange={() => setEditingProduct(null)}>
          <DialogContent className="bg-white max-w-md" data-testid="pricing-edit-product-dialog">
            <DialogHeader><DialogTitle>Edit Price — {editingProduct.description}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-gray-600">List Price (£)</Label>
                <Input type="number" step="0.01" value={editForm.list_price} onChange={(e) => setEditForm({ ...editForm, list_price: parseFloat(e.target.value) || 0 })} data-testid="pricing-edit-price-input" />
              </div>
              <div>
                <Label className="text-xs text-gray-600">Effective Date</Label>
                <Input type="date" value={editForm.effective_date} onChange={(e) => setEditForm({ ...editForm, effective_date: e.target.value })} data-testid="pricing-edit-date-input" />
              </div>
              <div>
                <Label className="text-xs text-gray-600">Note (optional)</Label>
                <Input value={editForm.note} onChange={(e) => setEditForm({ ...editForm, note: e.target.value })} placeholder="e.g. Corrected typo, manual adjustment..." data-testid="pricing-edit-note-input" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingProduct(null)}>Cancel</Button>
              <Button onClick={submitEdit} style={{ backgroundColor: enconOrange }} className="text-white" data-testid="pricing-edit-save-btn">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {historyFor && (
        <Dialog open onOpenChange={() => setHistoryFor(null)}>
          <DialogContent className="bg-white max-w-md" data-testid="pricing-history-dialog">
            <DialogHeader><DialogTitle>Price History — {historyFor.description}</DialogTitle></DialogHeader>
            <div className="max-h-80 overflow-y-auto space-y-2">
              <div className="flex justify-between text-sm p-2 bg-orange-50 rounded font-semibold">
                <span>Current: £{(historyFor.list_price || 0).toFixed(2)}</span>
                <span>{historyFor.updated_label}</span>
              </div>
              {history.length === 0 && <p className="text-sm text-gray-400 text-center py-4">No previous price changes recorded.</p>}
              {[...history].reverse().map((h, i) => (
                <div key={i} className="flex justify-between text-sm p-2 border-b">
                  <span>£{(h.price || 0).toFixed(2)}{h.pct_applied ? ` (${h.pct_applied > 0 ? '+' : ''}${h.pct_applied}%)` : ''}</span>
                  <span className="text-gray-500">{h.effective_date}</span>
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default PricingProductTable;
